import './polyfills/index';

import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import { sharedStore } from './stores/shared-store';
import { Window } from './types/window';
import Config from './Config';
import AppLazy from './AppLazy';

declare const window: Window & globalThis.Window;

if (import.meta.env.VITE_APP_RELEASE) {
  if (!Element.prototype.remove) {
    Element.prototype.remove = function removeElement() {
      this.parentElement.removeChild(this);
    };
  }

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason as { code?: number; message?: string } | undefined;

    if (
      (reason?.code === 4900 &&
        reason?.message === 'The provider is disconnected from all chains.') ||
      (reason?.code === 4001 && reason?.message === 'wallet must has at least one account')
    ) {
      // Ignore noisy wallet provider errors triggered by browser extensions
      event.preventDefault();
    }
  });

  Sentry.init({
    dsn: `${import.meta.env.VITE_APP_SENTRY_API_URL}`,
    release: import.meta.env.VITE_APP_RELEASE,
    debug: Config.isDev(),
    allowUrls: ['livechat.com'],
    beforeSend(event) {
      const userAgent = navigator.userAgent || '';

      // Check if the user agent matches Chrome Mobile WebView
      const isChromeMobileWebView =
        /; wv\)/.test(userAgent) && /Chrome\/[\d.]+ Mobile/.test(userAgent);

      // Check for generic/automated Android devices (device family "K" or "Generic Smartphone")
      const isGenericAndroid = ['K', 'Generic Smartphone'].includes(event.contexts?.device?.family);

      // Check for very old browsers that don't support modern JS syntax (private fields, optional chaining, etc.)
      const chromeMatch = userAgent.match(/Chrome\/(\d+)\./);
      const chromeVersion = chromeMatch ? parseInt(chromeMatch[1], 10) : null;
      const isOldChrome = chromeVersion !== null && chromeVersion < 90;

      const firefoxMatch = userAgent.match(/Firefox\/(\d+)\./);
      const firefoxVersion = firefoxMatch ? parseInt(firefoxMatch[1], 10) : null;
      const isOldFirefox = firefoxVersion !== null && firefoxVersion < 90;

      const safariMatch = userAgent.match(/Version\/(\d+)\./);
      const safariVersion = safariMatch ? parseInt(safariMatch[1], 10) : null;
      const isOldSafari = safariVersion !== null && safariVersion < 15;

      // Check for old iOS versions (iOS < 15 doesn't support private class fields)
      const iosMatch = userAgent.match(/OS (\d+)[._]\d+/);
      const iosVersion = iosMatch ? parseInt(iosMatch[1], 10) : null;
      const isOldIOS = iosVersion !== null && iosVersion < 15;

      // Filter errors from anonymous/injected scripts that have no source files.
      // These come from browser extensions, proxies, antivirus software, or malicious code injection.
      // Stack traces show '<anonymous>' filename and cannot be debugged or fixed.
      // Common patterns: recursive stack overflows, syntax errors, corrupted function calls.
      const hasAnonymousFrames = event.exception?.values?.some((exception) =>
        exception.stacktrace?.frames?.some((frame) => frame.filename === '<anonymous>')
      );
      if (hasAnonymousFrames) {
        return null;
      }

      // If the user agent matches or device is a generic/automated Android, ignore the event by returning null
      if (
        isChromeMobileWebView ||
        isGenericAndroid ||
        isOldChrome ||
        isOldFirefox ||
        isOldSafari ||
        isOldIOS
      ) {
        return null;
      }

      // Otherwise, send the event to Sentry
      return event;
    },
    ignoreErrors: [
      /Свернуть is not defined/, // Ignore error related to Violentmonkey chrome extension
      /GM_config is not defined/, // Ignore error related to Violentmonkey chrome extension
      /Hotjar is not available, make sure init has been called/,
      /CrossStorageClient could not connect/, // https://github.com/zendesk/cross-storage/issues/58
      /Closing client. Could not access localStorage in hub./, // https://github.com/zendesk/cross-storage/issues/58
      /could not perform cross-storage:set/, // https://github.com/zendesk/cross-storage/issues/43
      /Load failed/, // https://stackoverflow.com/questions/71280168/javascript-typeerror-load-failed-error-when-calling-fetch-on-ios
      /NetworkError when attempting to fetch resource/, // Fetch issue caused by adblock - https://stackoverflow.com/a/62646075/4163255
      /Failed to read the 'localStorage' property from 'Window': Access is denied for this document./, // https://stackoverflow.com/questions/30481516/iframe-in-chrome-error-failed-to-read-localstorage-from-window-access-deni
      /Non-Error promise rejection captured with value: Object Not Found Matching Id/,
      /ResizeObserver loop completed with undelivered notifications/,
      /ResizeObserver loop limit exceeded/,
      /(subscriptions|subscription|billing data) couldn't be fetched/i,
      /rambox is not defined/, // Ignore error related to Rambox chrome extension
      /Method not found/, // Generic error from automation tools/extensions
      /(Invalid|Unexpected) (token|or)/, // Syntax errors from corrupted JS (extensions/proxies/antivirus)
      /Key not found/, // Firefox extension/crypto API errors
      /private (field|name)s? (are not currently supported|#\w+)/, // Old browsers don't support private class fields
      /Cannot parse class method with private name/, // Safari < 15 private fields error
      /Can't find variable: \$/, // jQuery errors from injected scripts (extensions/WebView host)
    ],
  });

  Sentry.setTag('version', 'global');
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<AppLazy />);

window.Accounts = Object.assign(window.Accounts ?? {}, {
  // This is public API of our React App; extend instead of overwriting native bridge methods
  setAgentLogin(agentLogin: string) {
    sharedStore.set({ email: agentLogin });
  },
});
